import { makeAutoObservable } from 'mobx'
import { toastStore } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import { Attachment } from 'entities/Attachment/model/Attachment'
import {
  ComplianceApi,
  type IOptInMethodTypes,
  type IResponseOptInUpload,
  type IResponseOptInData,
} from 'entities/Compliance'
import { type IResponseMedia } from 'entities/Attachment'
import type { PresentationModeStore } from 'widgets/PresentationMode'
import {
  IOptInMethodsMap,
  IOptInYTypeMap,
} from 'pages/settings/pages/compliance/pages/localNumbers/route/type'
import { LocalNumbersRoutes } from 'pages/settings/pages/compliance/pages/localNumbers'
import { type LocalNumbersStepsStore } from 'pages/settings/pages/compliance/pages/localNumbers/store/localNumbersStepsStore'
import { SettingsPath } from 'pages/settings/route/settingsPath'
import { CommonOptInContentStore } from 'pages/settings/pages/compliance/ui/CommonOptInContent/store/commonOptInContentStore'

const maxAttachmentsLength = 3

export class OptInContentStore {
  constructor(
    private _localNumbersStore: LocalNumbersStepsStore,
    private _presentationStore: PresentationModeStore
  ) {
    makeAutoObservable(this)
    this._commonOptInConsentStore = new CommonOptInContentStore(
      this._localNumbersStore.complianceLayerStore,
      this._presentationStore
    )
  }

  private _attachmentError: Map<LocalNumbersRoutes, { title?: string; desc?: string } | null> =
    new Map()
  private _attachmentErrorTimeouts: Map<LocalNumbersRoutes, number | null> = new Map()
  private _optInImages: Map<LocalNumbersRoutes, IResponseOptInUpload[] | null> = new Map()
  private _filesPreview: File[] = []
  private readonly _commonOptInConsentStore: CommonOptInContentStore

  get commonOptInConsentStore() {
    return this._commonOptInConsentStore
  }

  get complianceLayerStore() {
    return this._localNumbersStore.complianceLayerStore
  }

  get localNumbersStore() {
    return this._localNumbersStore
  }

  get isOnlineFormNextDisabled() {
    return Boolean(
      !this.commonOptInConsentStore.termsAndPrivacyAgreement ||
        this.getOptInAttachmentError(LocalNumbersRoutes.optInOnlineFormContent) ||
        !this.getOptInImages(LocalNumbersRoutes.optInOnlineFormContent)?.length ||
        !this.commonOptInConsentStore.isOnlineFormUrl ||
        this.commonOptInConsentStore.needCheckUrlAfterChange
    )
  }

  init = (optInData: IResponseOptInData[] | null) => {
    if (!optInData) return

    if (optInData) {
      optInData.forEach((item) => {
        const isCorrect = item.noPublishedOnlineFormUrl || Boolean(item.onlineFormUrl?.length)
        this.commonOptInConsentStore.setOnlineFormUrl(item.onlineFormUrl || '', isCorrect)
        this.commonOptInConsentStore.setNoPublishedOnlineFormUrl(
          item.noPublishedOnlineFormUrl || false
        )

        if (item.optInImages?.length) {
          this.setOptInImages(
            IOptInYTypeMap[item.optInMethod] as LocalNumbersRoutes,
            item.optInImages
          )
        }
      })
    }
  }

  getOptInAttachmentError = (type: LocalNumbersRoutes) => {
    return this._attachmentError.get(type) || null
  }

  getIsNextDisabled = (type: LocalNumbersRoutes) => {
    return this.getOptInImages(type)?.length === 0
  }

  clearAttachmentError = (type: LocalNumbersRoutes) => {
    this._attachmentError.set(type, null)

    const timeout = this._attachmentErrorTimeouts.get(type)
    if (timeout) {
      clearTimeout(timeout)
      this._attachmentErrorTimeouts.set(type, null)
    }
  }

  getOptInImages = (type: LocalNumbersRoutes) => {
    return this._optInImages.get(type) || []
  }

  getTargetImage = (type: LocalNumbersRoutes, sourceUrl: string) => {
    return this.getOptInImages(type).find((image) => image.sourceUrl === sourceUrl)
  }

  getOptInImagesAsAttach = (type: LocalNumbersRoutes) => {
    const previews = this._filesPreview.map(
      (file) =>
        new Attachment({
          file,
          loading: true,
        })
    )

    const getOptInImages = this.getOptInImages(type).map((attachment) => {
      return new Attachment({
        responseMedia: {
          size: attachment.size,
          source: attachment.sourceUrl,
          name: attachment.name,
          content_type: 'image',
        } as IResponseMedia,
      })
    })

    return [...getOptInImages, ...previews]
  }

  setOptInImages = (type: LocalNumbersRoutes, data: IResponseOptInUpload[] | null) => {
    this._optInImages.set(type, data)
  }

  addOptInImage = (type: LocalNumbersRoutes, data: IResponseOptInUpload) => {
    const lastImages = this._optInImages.get(type) || []

    this._optInImages.set(type, [...lastImages, data])
    this._filesPreview = this._filesPreview.filter((tempFile) => tempFile.name !== data.name)
  }

  setAttachmentError = (
    type: LocalNumbersRoutes,
    error: { title?: string; desc?: string } | null
  ) => {
    this.clearAttachmentError(type)
    this._attachmentError.set(type, error)

    if (error === null) return

    const timer = window.setTimeout(() => {
      this._attachmentError.set(type, null)
    }, 5000)

    this._attachmentErrorTimeouts.set(type, timer)
  }

  getUploadError = (files: File[], type: LocalNumbersRoutes) => {
    const lastImages = this._optInImages.get(type) || []

    const sizeFileByte = 3_931_904
    const sizeFileMB = (sizeFileByte / 1024 / 1024).toFixed(1)
    const sizeLimits = {
      'image/png': sizeFileByte,
      'image/jpeg': sizeFileByte,
    }

    const allowedTypes = ['image/png', 'image/jpeg']
    const noCorrectType = files.every((file) => !allowedTypes.includes(file.type))

    if (files.length + lastImages.length > maxAttachmentsLength) {
      return {
        desc: 'Please upload up to 3 images per opt-in method',
      }
    }

    if (noCorrectType) {
      return {
        title: 'The file is not supported for upload',
        desc: 'Please upload only: jpg, png',
      }
    }

    const maxSizeFile = files.find(
      (file) =>
        allowedTypes.includes(file.type) &&
        file.size > sizeLimits[file.type as keyof typeof sizeLimits]
    )

    if (maxSizeFile) {
      return {
        desc: `The image file is too large. It must not exceed ${sizeFileMB} MB`,
      }
    }

    return null
  }

  uploadOptInImageAttachment = async (files: File[], type: LocalNumbersRoutes) => {
    const error = this.getUploadError(files, type)

    if (error) {
      this.setAttachmentError(type, error)
      return
    }

    try {
      await Promise.all(
        files.map(async (file) => {
          this._filesPreview.push(file)

          const { data } = await ComplianceApi.setCampaignOptInFiles(file)

          this.addOptInImage(type, data[0])
          return { file, data }
        })
      )

      toastStore.add({
        title: `${files.length} file${files.length > 1 ? 's' : ''} uploaded`,
        type: 'success',
      })
    } catch (error) {
      logger.error(error)
    } finally {
      this._filesPreview = []
    }
  }

  campaignOptInContentNextClick = async (path: LocalNumbersRoutes, isCheckWithAI?: boolean) => {
    if (!this.localNumbersStore.campaignId) return

    const { isAiCheckrWillUse, setIsContinueWithIssues, analyzeAI10DLC } =
      this.localNumbersStore.aiSidebarStore

    try {
      this.localNumbersStore.stepsStore.setStepLoading(true)
      setIsContinueWithIssues(false)

      const optInImages = this.getOptInImages(path)

      if (!IOptInMethodsMap[path] || !optInImages) return

      const step = 4

      const { data } = await ComplianceApi.updateComplianceServicesCampaign({
        step,
        optInData: {
          optInMethod: IOptInMethodsMap[path] as IOptInMethodTypes,
          optInImages,
          onlineFormUrl: this.commonOptInConsentStore.onlineFormUrl,
          noPublishedOnlineFormUrl: this.commonOptInConsentStore.noPublishedOnlineFormUrl,
        },
        campaignId: this.localNumbersStore.campaignId,
      })

      this.localNumbersStore.complianceLayerStore.set10DLCServiceCampaign(data)

      if (isAiCheckrWillUse && isCheckWithAI) {
        const isShouldRedirect = await analyzeAI10DLC(step)

        if (!isShouldRedirect) return
      }

      const nextStep = this.localNumbersStore.getNextStep(path)

      uiStore.changeRoute({
        path: `/${SettingsPath.compliance.localNumbers.root}/${nextStep}`,
      })
    } catch (e) {
      logger.error(e)
    } finally {
      this._localNumbersStore.stepsStore.setStepLoading(false)
    }
  }

  deleteAttachment = async (sourceUrl: string, type: LocalNumbersRoutes) => {
    try {
      const targetImage = this.getTargetImage(type, sourceUrl)

      if (!targetImage) return

      const { data } = await ComplianceApi.deleteLocalNumberAttachment({
        sourceUrl,
        uid: targetImage.uid,
      })
      if (data.deleted) {
        const filteredAttachments =
          this.getOptInImages(type)?.filter((a) => a.sourceUrl !== sourceUrl) || []

        this.setOptInImages(type, filteredAttachments)
      }
    } catch (e) {
      logger.error(e)
    }
  }
}
